import React, { Component } from "react";
import client from "@culturehq/client";
import { ActionButton, Tag, Thumbnail, Tooltip } from "@culturehq/components";

import { useProfile } from "app/context";
import { DepartmentLink, ProfileLink } from "app/links";
import { isMicrosoft, t, Trans } from "app/utils";

import { Time } from "components/common/Time";
import ErrorModal from "components/display/ErrorModal";

import ChqModalConfirmDelete from "components/common/ChqModalConfirmDelete";
import getUserName from "app/utils/getUserName";
import UserEditModal from "./UserEditModal";

const UserStatus = ({ user }) => {
  if (!user.active) {
    return t("Disabled");
  }

  if (user.visibility === "pending" || user.visibility === "unapproved") {
    return t("Unapproved");
  }

  if (user.claimed) {
    return t("Enabled");
  }

  // we haven't sent an invitation yet
  if (!user.inviteExpiresAt) {
    return t("Enabled");
  }

  if (user.inviteExpiresAt && new Date() > new Date(user.inviteExpiresAt)) {
    return (
      <Tooltip tip={t("For security purposes, users have seven days to create an account before their invite must be resent.")}>
        <Trans>Invite Expired</Trans>
      </Tooltip>
    );
  }

  return (
    <Tooltip tip={t("For security purposes, users have seven days to create an account before their invite must be resent.")}>
      <Trans>Invite Pending</Trans>
      <br />
      Expires <Time time={user.inviteExpiresAt} format="%b %e" />
    </Tooltip>
  );
};

class UserRowContext extends Component {
  state = {
    error: null,
    inviteResent: false,
    editOpen: false
  };

  handleDeactivateUser = async () => {
    const { user: { id: userId }, onUpdate } = this.props;

    try {
      const { user } = await client.deactivateUser({ userId });
      onUpdate(user);
    } catch (error) {
      this.setState({ error });
    }
  };

  handleReactivateUser = async () => {
    const { user: { id: userId }, onUpdate } = this.props;

    try {
      const { user } = await client.reactivateUser({ userId });
      onUpdate(user);
    } catch (error) {
      this.setState({ error });
    }
  };

  handleResendInvite = async () => {
    const { user, onUpdate } = this.props;

    try {
      const { invite } = await client.createUserInvite({ userId: user.id });

      this.setState({ inviteResent: true });
      onUpdate({ ...user, inviteExpiresAt: invite.expiresAt });
    } catch (error) {
      this.setState({ error });
    }
  };

  handleDeleteUser = async () => {
    const { user, onDelete } = this.props;

    await client.deleteUser({ userId: user.id });
    onDelete(user);
  };

  handleErrorModalClose = () => {
    this.setState({ error: null });
  };

  handleEditChange = user => {
    const { onUpdate } = this.props;

    this.setState({ editOpen: false });
    onUpdate(user);
  };

  handleEditOpen = () => {
    this.setState({ editOpen: true });
  };

  handleEditClose = () => {
    this.setState({ editOpen: false });
  };

  render() {
    const { user, profile } = this.props;
    const { inviteResent, error, editOpen } = this.state;
    const { culturehqAdmin, organization, organizationAdmin } = profile;

    return (
      <>
        <tr className={user.active ? "enabled" : "disabled"}>
          <td>
            <Thumbnail image={user.avatar.thumbUrl} title={getUserName(user)} />
            <ErrorModal error={error} onClose={this.handleErrorModalClose} />
          </td>
          <td>
            <ProfileLink user={user} />
            {!user.active && (
              <>
                {" "}<Tag color="gray"><Trans>Disabled</Trans></Tag>
              </>
            )}
            {user.organizationAdmin && (
              <>
                {" "}<Tag color="blue"><Trans>Admin</Trans></Tag>
              </>
            )}
            {user.visibility === "pending" && (
              <>
                {" "}<Tag color="red"><Trans>Pending</Trans></Tag>
              </>
            )}
            <br />
            {user.email}
          </td>
          <td>{user.createdStoriesNumber}</td>
          <td>
            {user.departments.map((department, key) => (
              <span key={department.id}>
                <DepartmentLink department={department} />
                {key !== user.departments.length - 1 && ", "}
              </span>
            ))}
          </td>
          <td>{user.location && user.location.name}</td>
          <td>
            <UserStatus user={user} />
          </td>
          <td className="actions">
            {user.active ? (
              <ActionButton onClick={this.handleDeactivateUser}>
                <Trans>Disable</Trans>
              </ActionButton>
            ) : (
              <ActionButton onClick={this.handleReactivateUser}>
                <Trans>Re-Enable</Trans>
              </ActionButton>
            )}

            {user.active && (
              <ActionButton onClick={this.handleEditOpen}>
                <Trans>Edit</Trans>
              </ActionButton>
            )}

            {(culturehqAdmin || (isMicrosoft(organization.name) && organizationAdmin)) && (
              <ChqModalConfirmDelete
                onAccept={this.handleDeleteUser}
                trigger={onTrigger => (
                  <ActionButton onClick={onTrigger}>
                    <Trans>Delete</Trans>
                  </ActionButton>
                )}
              >
                <p>
                  <Trans>Are you sure you want to delete {getUserName(user)}?</Trans>
                </p>
                <p>
                  <Trans>
                    This will permanently delete the user, anything they&apos;ve contributed,
                    and remove their content from any page it&apos;s embedded.
                  </Trans>
                </p>
              </ChqModalConfirmDelete>
            )}

            {(!inviteResent && (user.inviteExpiresAt && !user.claimed)) && (
              <ActionButton onClick={this.handleResendInvite} className="display-block">
                <Trans>Re-invite</Trans>
              </ActionButton>
            )}
          </td>
        </tr>
        {editOpen && (
          <UserEditModal
            user={user}
            onChange={this.handleEditChange}
            onClose={this.handleEditClose}
          />
        )}
      </>
    );
  }
}

const UserRow = ({ user, onUpdate, onDelete }) => {
  const { profile } = useProfile();

  return (
    <UserRowContext
      user={user}
      onUpdate={onUpdate}
      onDelete={onDelete}
      profile={profile}
    />
  );
};

export default UserRow;
